import { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer, Size } from 'react-virtualized';
import { INDICATOR } from '@voladeum/client';
import { Chart as ChartJS, registerables } from 'chart.js';
import { selectSignal } from 'ducks/signal/selectors';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppSelector } from 'store/hooks';
import { filterTimeseries, getMinMax } from 'utils/arrays';
import { getLineAxis, getLineDataset, getTimeseriesAxis } from 'utils/charts';

import 'chartjs-adapter-moment';

import { EMPTY_ARRAY } from '../../../../../constants';

import { AdvancedGraphProps } from './types';

import cn from './IndicatorsGraph.module.scss';

ChartJS.register(...registerables);

export const OBVGraph = ({ candles = EMPTY_ARRAY, visible }: AdvancedGraphProps) => {
  const obvRef = useRef<ChartJS>(null);
  const signals = useAppSelector(selectSignal);

  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  const obv = useMemo(() => {
    const combined_indicators = [...strategy_indicators, ...signal_indicators];
    return combined_indicators
      .filter((indicator) => indicator.indicator_id === INDICATOR.OBV)
      .flatMap((indicator) => getLineDataset(candles, signals, indicator));
  }, [candles, signal_indicators, signals, strategy_indicators]);

  const { min, max } = useMemo(() => {
    const values = obv.flatMap((dataset) => filterTimeseries(dataset.data, visible));
    return getMinMax(values);
  }, [obv, visible]);

  const obv_options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      line: getLineAxis({ min, max, buffer: 0.2 }),
    },
    plugins: { legend: { display: false } },
  };

  return (
    <AutoSizer>
      {({ height, width }: Size) => (
        <div className={cn.graph} style={{ height, width }}>
          <Chart
            key={`obv-${height}-${width}`}
            ref={obvRef}
            type="line"
            data={{ labels: [], datasets: obv }}
            options={obv_options}
            width={width}
            height={height}
          />
        </div>
      )}
    </AutoSizer>
  );
};
